import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Alert, Badge, Button, Card, Col, Form, Modal, ProgressBar, Row, Spinner, Table} from "react-bootstrap";
import {Bar, Doughnut, Line} from "react-chartjs-2";
import {
    ArcElement,
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    Tooltip,
} from "chart.js";
import {Link} from "react-router-dom";
import {useAccounts} from "../../contextjs/AccountContext";
import {useUser} from "../../UserContext";
import {
    createExpenseBudget,
    deleteExpenseBudget,
    fetchExpenseBudgetReport,
    fetchExpenseBudgets,
    fetchFinancialYears,
    stopExpenseBudgetRecurrence,
    updateExpenseBudget,
} from "../../api/api";
import {AccountingScopeSelect, useAccountingScope} from "./accountingscope";
import "./budgets.css";

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, LineElement, PointElement, Tooltip, Legend);

const pad = (value) => String(value).padStart(2, "0");
const currentMonth = () => {
    const today = new Date();
    return `${today.getFullYear()}-${pad(today.getMonth() + 1)}`;
};
const monthStart = (month) => `${month}-01`;
const monthEnd = (month) => {
    const [year, monthNumber] = month.split("-").map(Number);
    return `${year}-${pad(monthNumber)}-${pad(new Date(year, monthNumber, 0).getDate())}`;
};
const money = (value) => Number(value || 0).toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0});
const percentage = (value) => value === null || value === undefined ? "—" : `${Number(value).toFixed(1)}%`;
const palette = ["#1f7a5a", "#45a36f", "#f0a202", "#e4572e", "#4f6dff", "#7b61ff", "#2f4858", "#79c2d0"];
const monthsInPeriod = (startDate, endDate) => {
    if (!startDate || !endDate) return 1;
    const [startYear, startMonth] = startDate.slice(0, 7).split("-").map(Number);
    const [endYear, endMonth] = endDate.slice(0, 7).split("-").map(Number);
    return Math.max(((endYear - startYear) * 12) + endMonth - startMonth + 1, 1);
};

const Budgets = () => {
    const scope = useAccountingScope();
    const {user} = useUser();
    const {accounts} = useAccounts();
    const [startDate, setStartDate] = useState(monthStart(currentMonth()));
    const [endDate, setEndDate] = useState(monthEnd(currentMonth()));
    const [excludeTillCharges, setExcludeTillCharges] = useState(true);
    const [report, setReport] = useState(null);
    const [budgets, setBudgets] = useState([]);
    const [budgetMonth, setBudgetMonth] = useState(currentMonth());
    const [formMonth, setFormMonth] = useState(currentMonth());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [showBudgetForm, setShowBudgetForm] = useState(false);
    const [editingBudget, setEditingBudget] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({store: "", account: "", budget_ceiling: "", target_ceiling: "", recurs_monthly: false, notes: ""});
    const [pastFinancialYears, setPastFinancialYears] = useState([]);
    const [historicalYearId, setHistoricalYearId] = useState("");
    const [historicalReport, setHistoricalReport] = useState(null);
    const [historicalLoading, setHistoricalLoading] = useState(false);
    const [historicalError, setHistoricalError] = useState("");

    const isAdmin = Boolean(user?.is_superuser || user?.groups?.includes?.("Admin") || user?.groups?.includes?.("Administrator"));
    const expenseAccounts = useMemo(() => accounts.filter((account) =>
        account.account_type_details?.account_type_name === "Expense" &&
        !["Cost of Goods Sold", "Purchase"].includes(account.account_name)
    ), [accounts]);

    const loadBudgets = useCallback(async () => {
        if (scope.loadingScope || !scope.storeId) return;
        try {
            const data = await fetchExpenseBudgets({store_id: scope.storeId, period_start: monthStart(budgetMonth)});
            setBudgets(data);
        } catch (loadError) {
            setError(loadError?.response?.data?.detail || "Unable to load budget ceilings.");
        }
    }, [scope.loadingScope, scope.storeId, budgetMonth]);

    useEffect(() => {
        if (scope.loadingScope || !scope.storeId) return undefined;
        const timer = setTimeout(async () => {
            setLoading(true);
            setError("");
            try {
                const data = await fetchExpenseBudgetReport({
                    store_id: scope.storeId,
                    start_date: startDate,
                    end_date: endDate,
                    exclude_till_charges: excludeTillCharges,
                });
                setReport(data);
            } catch (loadError) {
                setReport(null);
                setError(loadError?.response?.data?.detail || "Unable to build the budget report.");
            } finally {
                setLoading(false);
            }
        }, 250);
        return () => clearTimeout(timer);
    }, [scope.loadingScope, scope.storeId, startDate, endDate, excludeTillCharges]);

    useEffect(() => { loadBudgets(); }, [loadBudgets]);

    useEffect(() => {
        if (scope.storeId && scope.storeId !== "all") {
            setForm((current) => ({...current, store: String(scope.storeId)}));
        }
    }, [scope.storeId]);

    useEffect(() => {
        let active = true;
        fetchFinancialYears()
            .then((response) => {
                if (!active) return;
                const rows = Array.isArray(response) ? response : (response?.results || []);
                const today = new Date().toISOString().slice(0, 10);
                const past = rows.filter((year) => year.is_closed || year.end_date < today);
                setPastFinancialYears(past);
                setHistoricalYearId((current) => current || String(past[0]?.id || ""));
            })
            .catch(() => active && setHistoricalError("Unable to load past financial years."));
        return () => { active = false; };
    }, []);

    const historicalYear = useMemo(
        () => pastFinancialYears.find((year) => String(year.id) === String(historicalYearId)),
        [pastFinancialYears, historicalYearId]
    );

    useEffect(() => {
        if (scope.loadingScope || !scope.storeId || !historicalYear) {
            setHistoricalReport(null);
            return undefined;
        }
        let active = true;
        setHistoricalLoading(true);
        setHistoricalError("");
        fetchExpenseBudgetReport({
            store_id: scope.storeId,
            financial_year_id: historicalYear.id,
            exclude_till_charges: excludeTillCharges,
        })
            .then((data) => active && setHistoricalReport(data))
            .catch((loadError) => active && setHistoricalError(
                loadError?.response?.data?.detail || "Unable to build the historical financial-year report."
            ))
            .finally(() => active && setHistoricalLoading(false));
        return () => { active = false; };
    }, [scope.loadingScope, scope.storeId, historicalYear, excludeTillCharges]);

    const openNewBudget = () => {
        setEditingBudget(null);
        setFormMonth(budgetMonth);
        setForm({
            store: scope.storeId !== "all" ? String(scope.storeId || "") : "",
            account: "",
            budget_ceiling: "",
            target_ceiling: "",
            recurs_monthly: false,
            notes: "",
        });
        setShowBudgetForm(true);
    };

    const openEditBudget = (budget) => {
        setEditingBudget(budget);
        setFormMonth(String(budget.period_start).slice(0, 7));
        setForm({
            store: String(budget.store), account: String(budget.account),
            budget_ceiling: budget.budget_ceiling, target_ceiling: budget.target_ceiling,
            recurs_monthly: Boolean(budget.recurs_monthly),
            notes: budget.notes || "",
        });
        setShowBudgetForm(true);
    };

    const saveBudget = async (event) => {
        event.preventDefault();
        setSaving(true);
        setError("");
        const payload = {
            ...form,
            period_start: monthStart(formMonth),
            recurrence_end: form.recurs_monthly ? (editingBudget?.recurrence_end || null) : null,
        };
        try {
            if (editingBudget) {
                await updateExpenseBudget(editingBudget.id, {
                    target_ceiling: payload.target_ceiling,
                    budget_ceiling: payload.budget_ceiling,
                    recurs_monthly: payload.recurs_monthly,
                    recurrence_end: payload.recurrence_end,
                    notes: payload.notes,
                });
            } else {
                await createExpenseBudget(payload);
            }
            setShowBudgetForm(false);
            await loadBudgets();
            const refreshed = await fetchExpenseBudgetReport({
                store_id: scope.storeId, start_date: startDate, end_date: endDate,
                exclude_till_charges: excludeTillCharges,
            });
            setReport(refreshed);
        } catch (saveError) {
            const data = saveError?.response?.data;
            const firstFieldError = data && typeof data === "object"
                ? Object.values(data).flat().find((value) => typeof value === "string")
                : null;
            setError(firstFieldError || data?.detail || "Unable to save this budget.");
        } finally {
            setSaving(false);
        }
    };

    const removeBudget = async (budget) => {
        if (!window.confirm(`Delete the ${budget.account_name} budget for ${budget.store_name}?`)) return;
        try {
            await deleteExpenseBudget(budget.id, budget.store);
            await loadBudgets();
        } catch (deleteError) {
            setError(deleteError?.response?.data?.detail || "Unable to delete this budget.");
        }
    };

    const stopRecurrence = async (budget) => {
        if (!window.confirm(`Stop repeating ${budget.account_name} after ${budgetMonth}? The selected month remains covered.`)) return;
        try {
            await stopExpenseBudgetRecurrence(budget.id, monthStart(budgetMonth), budget.store);
            await loadBudgets();
            const refreshed = await fetchExpenseBudgetReport({
                store_id: scope.storeId, start_date: startDate, end_date: endDate,
                exclude_till_charges: excludeTillCharges,
            });
            setReport(refreshed);
        } catch (stopError) {
            const data = stopError?.response?.data;
            setError(data?.detail || data?.through_month?.[0] || "Unable to stop this recurring budget.");
        }
    };

    const historicalMonths = monthsInPeriod(historicalYear?.start_date, historicalYear?.end_date);
    const historicalAccounts = (historicalReport?.accounts || [])
        .slice()
        .sort((left, right) => Number(right.actual) - Number(left.actual));
    const historicalTotal = Number(historicalReport?.summary?.discretionary_expenses || 0);
    const historicalChartData = {
        labels: historicalAccounts.slice(0, 10).map((row) => row.account_name),
        datasets: [{
            label: `${historicalYear?.name || "Past year"} actual expense`,
            data: historicalAccounts.slice(0, 10).map((row) => Number(row.actual)),
            backgroundColor: historicalAccounts.slice(0, 10).map((_, index) => palette[index % palette.length]),
        }],
    };

    const prepareFromHistoricalAverage = (row) => {
        setHistoricalError("");
        const monthlyAverage = Math.max(Number(row.actual || 0) / historicalMonths, 0);
        const isOverallReference = scope.storeId === "all";
        const exactBudget = !isOverallReference && budgets.find((budget) =>
            String(budget.store) === String(scope.storeId) &&
            String(budget.account) === String(row.account_id) &&
            String(budget.period_start).slice(0, 7) === budgetMonth
        );
        setEditingBudget(exactBudget || null);
        setFormMonth(budgetMonth);
        setForm({
            store: isOverallReference ? "" : String(scope.storeId),
            account: String(row.account_id),
            target_ceiling: monthlyAverage.toFixed(2),
            budget_ceiling: (monthlyAverage * 1.10).toFixed(2),
            recurs_monthly: Boolean(exactBudget?.recurs_monthly),
            notes: `Starting point based on ${historicalYear.name}${isOverallReference ? " all-store" : ""} average monthly actual KES ${money(monthlyAverage)}; maximum includes 10% headroom.`,
        });
        setShowBudgetForm(true);
    };

    const summary = report?.summary || {};
    const accountRows = report?.accounts || [];
    const trend = report?.trend || [];
    const chartRows = accountRows.slice().sort((a, b) => Number(b.actual) - Number(a.actual)).slice(0, 10);
    const breakdownData = {
        labels: chartRows.map((row) => row.account_name),
        datasets: [{label: "Actual expense", data: chartRows.map((row) => Number(row.actual)), backgroundColor: chartRows.map((_, i) => palette[i % palette.length])}],
    };
    const comparisonData = {
        labels: chartRows.map((row) => row.account_name),
        datasets: [
            {label: "Actual", data: chartRows.map((row) => Number(row.actual)), backgroundColor: "#e4572e"},
            {label: "Target", data: chartRows.map((row) => Number(row.target_ceiling)), backgroundColor: "#f0a202"},
            {label: "Maximum ceiling", data: chartRows.map((row) => Number(row.budget_ceiling)), backgroundColor: "#1f7a5a"},
        ],
    };
    const trendData = {
        labels: trend.map((row) => row.date),
        datasets: [{label: "Daily discretionary expense", data: trend.map((row) => Number(row.amount)), borderColor: "#1f7a5a", backgroundColor: "rgba(31,122,90,.15)", fill: true, tension: .28}],
    };
    const utilized = Math.max(Number(summary.discretionary_expenses || 0), 0);
    const remaining = Math.max(Number(summary.remaining_budget || 0), 0);
    const utilizationData = {
        labels: ["Used", "Remaining"],
        datasets: [{data: utilized + remaining > 0 ? [utilized, remaining] : [1], backgroundColor: utilized + remaining > 0 ? ["#e4572e", "#dfe8e3"] : ["#e9ecef"], borderWidth: 0}],
    };

    const statusBadge = (status) => ({
        ON_TRACK: <Badge bg="success">On target</Badge>,
        ABOVE_TARGET: <Badge bg="warning" text="dark">Above target</Badge>,
        OVER_BUDGET: <Badge bg="danger">Over ceiling</Badge>,
        UNBUDGETED: <Badge bg="secondary">Unbudgeted</Badge>,
    }[status] || <Badge bg="secondary">{status}</Badge>);

    return <div className="budget-workspace container-fluid px-3 px-xl-4 py-4">
        <div className="budget-hero mb-4">
            <div>
                <span className="budget-eyebrow">Financial planning</span>
                <h1>Expense budgets & capacity</h1>
                <p>Turn recorded expenses and generated profit into practical spending guardrails. COGS stays outside this plan because stock purchases determine it.</p>
            </div>
            <div className="d-flex flex-wrap gap-2">
                <Button as={Link} to="/accounting/expense" variant="light">Expense entries</Button>
                {isAdmin && <Button variant="warning" onClick={openNewBudget}>Set a ceiling</Button>}
            </div>
        </div>

        <Card className="budget-filter-card border-0 shadow-sm mb-4"><Card.Body>
            <Row className="g-3 align-items-end">
                {scope.isAdmin && <Col xl={3}><AccountingScopeSelect scope={scope} label="Planning scope" /></Col>}
                <Col md={3} xl={2}><Form.Label>From</Form.Label><Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} /></Col>
                <Col md={3} xl={2}><Form.Label>To</Form.Label><Form.Control type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} /></Col>
                <Col md={4} xl={3} className="pb-2"><Form.Check type="switch" id="budget-hide-till" label="Exclude Till transaction charges" checked={excludeTillCharges} onChange={(e) => setExcludeTillCharges(e.target.checked)} /></Col>
                <Col md={2} xl={2}><Button variant="outline-secondary" className="w-100" onClick={() => { const month = currentMonth(); setStartDate(monthStart(month)); setEndDate(monthEnd(month)); }}>This month</Button></Col>
            </Row>
        </Card.Body></Card>

        {scope.scopeError && <Alert variant="danger">{scope.scopeError}</Alert>}
        {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}
        {loading && <div className="text-center py-5"><Spinner animation="border" variant="success" /><div className="text-muted mt-2">Building the report…</div></div>}

        {!loading && report && <>
            <Row className="g-3 mb-4">
                <Col sm={6} xl={3}><Card className="metric-card metric-profit h-100"><Card.Body><span>Profit generated</span><strong>KES {money(summary.profit_generated)}</strong><small>Revenue less all expenses, including COGS</small></Card.Body></Card></Col>
                <Col sm={6} xl={3}><Card className="metric-card h-100"><Card.Body><span>Discretionary spend</span><strong>KES {money(summary.discretionary_expenses)}</strong><small>{excludeTillCharges ? "Till charges excluded" : "Till charges included"}</small></Card.Body></Card></Col>
                <Col sm={6} xl={3}><Card className="metric-card h-100"><Card.Body><span>Maximum budget</span><strong>KES {money(summary.budget_ceiling)}</strong><small>{percentage(summary.budget_utilization_percentage)} utilized</small></Card.Body></Card></Col>
                <Col sm={6} xl={3}><Card className="metric-card metric-capacity h-100"><Card.Body><span>Future expense capacity</span><strong>KES {money(summary.future_expense_capacity)}</strong><small>Lower of available profit and remaining ceiling</small></Card.Body></Card></Col>
            </Row>

            <Row className="g-4 mb-4">
                <Col xl={8}><Card className="border-0 shadow-sm h-100"><Card.Body><Card.Title>Actual vs target and ceiling</Card.Title><div className="chart-frame"><Bar data={comparisonData} options={{maintainAspectRatio: false, responsive: true, scales: {y: {beginAtZero: true}}}} /></div></Card.Body></Card></Col>
                <Col xl={4}><Card className="border-0 shadow-sm h-100"><Card.Body><Card.Title>Budget utilization</Card.Title><div className="doughnut-frame"><Doughnut data={utilizationData} options={{maintainAspectRatio: false, cutout: "70%", plugins: {legend: {position: "bottom"}}}} /></div><div className="text-center mt-3"><strong>KES {money(summary.remaining_budget)}</strong><div className="text-muted small">remaining maximum budget</div></div></Card.Body></Card></Col>
            </Row>

            <Row className="g-4 mb-4">
                <Col xl={7}><Card className="border-0 shadow-sm h-100"><Card.Body><Card.Title>Expense trend</Card.Title><div className="chart-frame"><Line data={trendData} options={{maintainAspectRatio: false, responsive: true, scales: {y: {beginAtZero: true}}}} /></div></Card.Body></Card></Col>
                <Col xl={5}><Card className="border-0 shadow-sm h-100"><Card.Body><Card.Title>Planning outlook</Card.Title>
                    <div className="outlook-row"><span>Target ceiling</span><strong>KES {money(summary.target_ceiling)}</strong></div>
                    <div className="outlook-row"><span>Remaining to target</span><strong className={Number(summary.remaining_target) < 0 ? "text-danger" : ""}>KES {money(summary.remaining_target)}</strong></div>
                    <div className="outlook-row"><span>Daily run rate</span><strong>KES {money(summary.daily_expense_run_rate)}</strong></div>
                    <div className="outlook-row"><span>Projected period expense</span><strong>KES {money(summary.projected_period_expense)}</strong></div>
                    <Alert variant={Number(summary.profit_generated) > 0 ? "success" : "warning"} className="mt-3 mb-0">
                        {Number(summary.profit_generated) > 0
                            ? `Current profit can support up to KES ${money(summary.future_expense_capacity)} of further governed expense without exceeding the available ceiling.`
                            : "The selected period has not generated positive profit. Additional discretionary commitments should be reviewed before approval."}
                    </Alert>
                </Card.Body></Card></Col>
            </Row>

            <Card className="border-0 shadow-sm mb-4"><Card.Body><Card.Title>Expense-account control report</Card.Title><Card.Subtitle className="text-muted mb-3">Net debit expense by account. Reversals reduce actual spend.</Card.Subtitle>
                <Table responsive hover className="align-middle"><thead><tr><th>Expense account</th><th>Status</th><th className="text-end">Actual</th><th className="text-end">Target</th><th className="text-end">Maximum</th><th>Utilization</th><th className="text-end">Entries</th></tr></thead>
                    <tbody>{accountRows.map((row) => <tr key={row.account_id}><td className="fw-semibold">{row.account_name}</td><td>{statusBadge(row.status)}</td><td className="text-end">KES {money(row.actual)}</td><td className="text-end">KES {money(row.target_ceiling)}</td><td className="text-end">KES {money(row.budget_ceiling)}</td><td style={{minWidth: 170}}><div className="small mb-1">{percentage(row.utilization_percentage)}</div><ProgressBar now={Math.min(Math.max(Number(row.utilization_percentage || 0), 0), 100)} variant={row.status === "OVER_BUDGET" ? "danger" : row.status === "ABOVE_TARGET" ? "warning" : "success"} /></td><td className="text-end">{row.entry_count}</td></tr>)}</tbody>
                </Table>
                {!accountRows.length && <div className="text-center text-muted py-4">No discretionary expense or budget data exists for this selection.</div>}
            </Card.Body></Card>

            <Row className="g-4 mb-4"><Col xl={7}><Card className="border-0 shadow-sm"><Card.Body><Card.Title>Expense composition</Card.Title><div className="chart-frame"><Bar data={breakdownData} options={{indexAxis: "y", maintainAspectRatio: false, responsive: true, plugins: {legend: {display: false}}, scales: {x: {beginAtZero: true}}}} /></div></Card.Body></Card></Col>
                <Col xl={5}><Card className="border-0 shadow-sm"><Card.Body><Card.Title>What the capacity means</Card.Title><p className="text-muted">Profit capacity is based on revenue less every expense, including COGS. The discretionary report excludes COGS and Purchase because those arise from trading activity rather than an administrative spending ceiling.</p><p className="text-muted mb-0">A positive number is a planning guardrail, not cash availability. Confirm current-asset balances and upcoming liabilities before committing future spend.</p></Card.Body></Card></Col></Row>
        </>}

        <Card className="history-card border-0 shadow-sm mb-4"><Card.Body>
            <div className="d-flex flex-wrap justify-content-between align-items-end gap-3 mb-3">
                <div><Card.Title className="mb-1">Past financial-year reference</Card.Title><Card.Subtitle className="text-muted">Use prior actual spending as evidence when setting this month's target and maximum ceiling.</Card.Subtitle></div>
                <Form.Group className="history-year-select"><Form.Label>Financial year</Form.Label><Form.Select value={historicalYearId} onChange={(event) => setHistoricalYearId(event.target.value)} disabled={!pastFinancialYears.length}>
                    {!pastFinancialYears.length && <option value="">No past financial year</option>}
                    {pastFinancialYears.map((year) => <option key={year.id} value={year.id}>{year.name} · {year.start_date} to {year.end_date}</option>)}
                </Form.Select></Form.Group>
            </div>
            {historicalError && <Alert variant="warning">{historicalError}</Alert>}
            {historicalLoading && <div className="text-center py-5"><Spinner animation="border" variant="success" /><div className="text-muted mt-2">Analysing the past financial year…</div></div>}
            {!historicalLoading && historicalReport && <>
                <Row className="g-3 mb-4">
                    <Col sm={6} xl={3}><div className="history-stat"><span>Discretionary expense</span><strong>KES {money(historicalTotal)}</strong></div></Col>
                    <Col sm={6} xl={3}><div className="history-stat"><span>Average per month</span><strong>KES {money(historicalTotal / historicalMonths)}</strong></div></Col>
                    <Col sm={6} xl={3}><div className="history-stat"><span>Profit generated</span><strong>KES {money(historicalReport.summary?.profit_generated)}</strong></div></Col>
                    <Col sm={6} xl={3}><div className="history-stat"><span>Reference period</span><strong>{historicalMonths} month{historicalMonths === 1 ? "" : "s"}</strong></div></Col>
                </Row>
                <Row className="g-4">
                    <Col xl={5}><div className="chart-frame"><Bar data={historicalChartData} options={{indexAxis: "y", maintainAspectRatio: false, responsive: true, plugins: {legend: {display: false}}, scales: {x: {beginAtZero: true}}}} /></div></Col>
                    <Col xl={7}><Table responsive hover className="align-middle"><thead><tr><th>Expense account</th><th className="text-end">Year actual</th><th className="text-end">Monthly average</th><th className="text-end">Share</th><th className="text-end">Entries</th>{isAdmin && <th></th>}</tr></thead>
                        <tbody>{historicalAccounts.map((row) => {
                            const monthlyAverage = Number(row.actual || 0) / historicalMonths;
                            const share = historicalTotal ? (Number(row.actual || 0) / historicalTotal) * 100 : 0;
                            return <tr key={row.account_id}><td className="fw-semibold">{row.account_name}</td><td className="text-end">KES {money(row.actual)}</td><td className="text-end">KES {money(monthlyAverage)}</td><td className="text-end">{percentage(share)}</td><td className="text-end">{row.entry_count}</td>{isAdmin && <td className="text-end"><Button size="sm" variant="outline-success" onClick={() => prepareFromHistoricalAverage(row)}>Use average</Button></td>}</tr>;
                        })}</tbody>
                    </Table></Col>
                </Row>
                {!historicalAccounts.length && <div className="text-center text-muted py-4">No discretionary expenses were recorded for this financial year and scope.</div>}
                <div className="small text-muted mt-3">“Use average” pre-fills the target with the historical monthly average and the maximum with 10% headroom. Review both figures before saving. COGS remains excluded, and the Till-charge switch above also applies here.</div>
            </>}
        </Card.Body></Card>

        <Card className="border-0 shadow-sm"><Card.Body>
            <div className="d-flex flex-wrap justify-content-between align-items-end gap-3 mb-3"><div><Card.Title className="mb-1">Monthly ceiling register</Card.Title><Card.Subtitle className="text-muted">Targets are the preferred limit; maximum ceilings are the hard guardrail. Recurring budgets carry forward until stopped.</Card.Subtitle></div><div className="d-flex gap-2 align-items-end"><Form.Group><Form.Label>Budget month</Form.Label><Form.Control type="month" value={budgetMonth} onChange={(e) => setBudgetMonth(e.target.value)} /></Form.Group>{isAdmin && <Button variant="success" onClick={openNewBudget}>Add budget</Button>}</div></div>
            <Table responsive hover><thead><tr><th>Store</th><th>Expense account</th><th className="text-end">Target</th><th className="text-end">Maximum ceiling</th><th>Schedule</th><th>Notes</th>{isAdmin && <th></th>}</tr></thead><tbody>{budgets.map((budget) => <tr key={budget.id}><td>{budget.store_name}</td><td className="fw-semibold">{budget.account_name}</td><td className="text-end">KES {money(budget.target_ceiling)}</td><td className="text-end">KES {money(budget.budget_ceiling)}</td><td>{budget.recurs_monthly ? <><Badge bg={budget.recurrence_end ? "secondary" : "success"}>{budget.recurrence_end ? "Stopped" : "Monthly"}</Badge><div className="small text-muted mt-1">From {String(budget.period_start).slice(0, 7)}{budget.recurrence_end ? ` through ${String(budget.recurrence_end).slice(0, 7)}` : " onward"}</div></> : <Badge bg="light" text="dark">This month only</Badge>}</td><td>{budget.notes || "—"}</td>{isAdmin && <td className="text-nowrap"><Button size="sm" variant="outline-primary" className="me-2" onClick={() => openEditBudget(budget)}>Edit</Button>{budget.recurs_monthly && !budget.recurrence_end && <Button size="sm" variant="outline-warning" className="me-2" onClick={() => stopRecurrence(budget)}>Stop</Button>}<Button size="sm" variant="outline-danger" onClick={() => removeBudget(budget)}>Delete</Button></td>}</tr>)}</tbody></Table>
            {!budgets.length && <div className="text-center text-muted py-4">No ceilings have been entered for {budgetMonth} and this scope.</div>}
        </Card.Body></Card>

        <Modal show={showBudgetForm} onHide={() => setShowBudgetForm(false)} centered><Form onSubmit={saveBudget}><Modal.Header closeButton><Modal.Title>{editingBudget ? "Edit" : "Create"} expense budget</Modal.Title></Modal.Header><Modal.Body>
            <Form.Group className="mb-3"><Form.Label>Store</Form.Label><Form.Select required value={form.store} onChange={(e) => setForm({...form, store: e.target.value})} disabled={Boolean(editingBudget)}><option value="">Select store</option>{scope.stores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}</Form.Select></Form.Group>
            <Form.Group className="mb-3"><Form.Label>Starting month</Form.Label><Form.Control required type="month" value={formMonth} onChange={(e) => setFormMonth(e.target.value)} disabled={Boolean(editingBudget)} /></Form.Group>
            <Form.Group className="mb-3"><Form.Label>Expense account</Form.Label><Form.Select required value={form.account} onChange={(e) => setForm({...form, account: e.target.value})} disabled={Boolean(editingBudget)}><option value="">Select account</option>{expenseAccounts.map((account) => <option key={account.id} value={account.id}>{account.account_name}</option>)}</Form.Select></Form.Group>
            <Row><Col><Form.Group className="mb-3"><Form.Label>Target ceiling</Form.Label><Form.Control required min="0" step="0.01" type="number" value={form.target_ceiling} onChange={(e) => setForm({...form, target_ceiling: e.target.value})} /></Form.Group></Col><Col><Form.Group className="mb-3"><Form.Label>Maximum ceiling</Form.Label><Form.Control required min="0" step="0.01" type="number" value={form.budget_ceiling} onChange={(e) => setForm({...form, budget_ceiling: e.target.value})} /></Form.Group></Col></Row>
            <Form.Check className="mb-3" type="switch" id="budget-recurs-monthly" label="Repeat this budget every month until stopped" checked={form.recurs_monthly} onChange={(e) => setForm({...form, recurs_monthly: e.target.checked})} disabled={Boolean(editingBudget?.recurs_monthly)} />
            {editingBudget?.recurrence_end && <Alert variant="secondary" className="py-2">This recurrence ended in {String(editingBudget.recurrence_end).slice(0, 7)}. Editing changes its amounts but does not restart it.</Alert>}
            {editingBudget?.recurs_monthly && !editingBudget?.recurrence_end && <Alert variant="warning" className="py-2">Changing this active recurring budget updates the rule for every month it covers.</Alert>}
            <Form.Group><Form.Label>Planning notes</Form.Label><Form.Control as="textarea" rows={3} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} /></Form.Group>
        </Modal.Body><Modal.Footer><Button variant="secondary" onClick={() => setShowBudgetForm(false)}>Cancel</Button><Button type="submit" variant="success" disabled={saving}>{saving ? "Saving…" : "Save budget"}</Button></Modal.Footer></Form></Modal>
    </div>;
};

export default Budgets;
